import keyBy from 'lodash/keyBy';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { performSearchIssueTypes, type SearchIssueType } from './SearchIssueType.types.tsx';
import type { IssueType, State } from './IssueType.types.tsx';

const PAGE_SIZE = 100;
// The picker paginates by offset with no "isLast" signal, so arbitrary upper bound.
const MAX_PAGES = 50;

const toIssueType = ({ id, name, avatarId, avatarUrl }: SearchIssueType): IssueType => ({
	id,
	name,
	description: '',
	avatarId: String(avatarId),
	avatarUrl,
	entityId: '',
	hierarchyLevel: 0,
	operation: {
		deletable: false,
		editable: false,
	},
});

/**
 * Fetches every issue type visible to the user across the whole site, independent of any project.
 * Used by global views, which are not bound to a project and therefore have no project ids to fan
 * a per-project fetch over.
 */
export const fetchAllIssueTypes =
	() =>
	async ({ setState, getState }: StoreActionApi<State>): Promise<void> => {
		try {
			const collected: SearchIssueType[] = [];

			for (let page = 0; page < MAX_PAGES; page++) {
				// Offset pagination is inherently sequential
				// oxlint-disable-next-line no-await-in-loop
				const { issueTypes } = await performSearchIssueTypes({
					nameContains: '',
					pagination: { startAt: page * PAGE_SIZE, max: PAGE_SIZE },
				});
				collected.push(...issueTypes);
				if (issueTypes.length < PAGE_SIZE) {
					break;
				}
			}

			setState({
				issueTypesById: {
					...getState().issueTypesById,
					...keyBy(collected.map(toIssueType), ({ id }) => id),
				},
			});
		} catch (maybeError) {
			const error =
				maybeError instanceof Error
					? maybeError
					: new Error('Error while fetching all issue types');

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.fetch-all-issue-types',
				'Failed to fetch all issue types',
				error,
			);

			throw error;
		}
	};
